import React, { useState, useEffect, useMemo } from "react";
import MyModal from "components/MyModal";
import * as yup from "yup";
import {
  indetityApiAppToken,
  useCreatePSPUserMutation,
  useGetUsersInvitationsQuery,
  useInviteUserMutation,
  useLazyGetUsersInvitationsQuery,
  useRequestChangePasswordMutation,
} from "redux/services/identity";
import {
  useToast,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  Select,
} from "@chakra-ui/react";
import Alert from "components/Alert";
import { useDispatch } from "react-redux";
import {
  useGetOrganizationQuery,
  useGetProfileQuery,
  useGetUserMutation,
  useGetUsersLazyQuery,
  useUpdateOrganisationMutation,
} from "redux/services/membership";
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE, Roles } from "common";
import { getOrganisationMembership } from "utils";
import { useRoles } from "hooks/useRoles";
import { getTotalRecordsErrorMessage } from "containers/CreateEnterpriseUser/CreateEnterpriseUser.helper";
import { setError } from "redux/slices/errorSlice";
import { useHistory } from "react-router-dom";

interface CreatePSPUserProps {
  readonly isOpen: boolean;
  readonly setOpenModalCreate: () => void;
}

type PSPField = 'firstName' | 'lastName' | 'emailAddress' | 'bussinessUnitName' | 'bussinessUnitCode';

const schema = yup.object().shape({
  firstName: yup.string().required("First Name is required"),
  lastName: yup.string().required("Last Name is required"),
  emailAddress: yup
    .string()
    .email("Invalid email")
    .required("Email is required"),
  bussinessUnitName: yup.string().required("PSP Name is required"),
  bussinessUnitCode: yup
    .string()
    .required("PSP Company Code is required")
    .matches(/^[A-Z0-9]+$/, "Only uppercase letters and numbers are allowed"),
});

const schemaWithPSPRole = yup.object().shape({
  firstName: yup.string().required("First Name is required"),
  lastName: yup.string().required("Last Name is required"),
  emailAddress: yup
    .string()
    .email("Invalid email")
    .required("Email is required"),
  userRole: yup.string().required("User Role is required"),
});

function CreatePSPUser(props: CreatePSPUserProps) {
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);

  const history = useHistory();
  const dispatch = useDispatch();
  const toast = useToast();
  const { isOpen, setOpenModalCreate } = props;
  const [
    getUser,
    {
      isSuccess: idGetUserSuccess,
      data: userData,
    },
  ] = useGetUserMutation();

  const [
    updateOranization,
    {
      isError: isUpdateOranisationError,
      error: updateOranisationError,
      isSuccess: isUpdateOranisationSuccess,
    },
  ] = useUpdateOrganisationMutation();

  const [
    createPSPUserMutation,
    {
      isSuccess: createUserSuccess,
      error: createUserError,
      data: createDataResonse,
    },
  ] = useCreatePSPUserMutation();

  const [
    requestChangePasswordMutation,
    {
      isSuccess: requestPasswordChangeSuccess,
    },
  ] = useRequestChangePasswordMutation();

  const [
    inviteUserMutation,
    {
      isSuccess: inviteUserSuccess,
      error: inviteUserError,
    },
  ] = useInviteUserMutation();

  const { data: profileData } = useGetProfileQuery();
  const membershipOrganisation = getOrganisationMembership(
    profileData?.data?.memberships
  );
  const { isPaymentOpsRole, isPSPAdminRole } = useRoles();

  const [formData, setFormData] = useState<{
    firstName: string;
    lastName: string;
    emailAddress: string;
    bussinessUnitName: string;
    bussinessUnitCode: string;
    userRole?: string;
  }>({
    firstName: "",
    lastName: "",
    emailAddress: "",
    bussinessUnitName: "",
    bussinessUnitCode: "",
    userRole: ""
  });

  const [errors, setErrors] = useState<{
    firstName?: string;
    lastName?: string;
    emailAddress?: string;
    bussinessUnitName?: string;
    bussinessUnitCode?: string;
    userRole?: string;
  }>({});

  const { data: organizationData } = useGetOrganizationQuery(
    {
      orgId: membershipOrganisation?.organisationId ?? "",
      userId: profileData?.data?.userId ?? "",
    },
    { skip: !(profileData && membershipOrganisation && !isPaymentOpsRole) }
  );

  const defaultParamsGetPendingUsers = useMemo(
    () => ({
      pageNumber: DEFAULT_PAGE,
      pageSize: DEFAULT_PAGE_SIZE,
      orgToken: membershipOrganisation?.token ?? "",
    }),
    [membershipOrganisation?.token]
  );

  const { refetch: refetchGetPendingUsers } = useGetUsersInvitationsQuery(
    defaultParamsGetPendingUsers,
    {
      refetchOnMountOrArgChange: false,
    }
  );

  const { refetch: refetchGetPendingMakers } = useGetUsersInvitationsQuery(
    {
      ...defaultParamsGetPendingUsers,
      userRole: [Roles.BUMaker],
    },
    {
      refetchOnMountOrArgChange: false,
    }
  );

  const { refetch: refetchGetPendingCheckers } = useGetUsersInvitationsQuery(
    {
      ...defaultParamsGetPendingUsers,
      userRole: [Roles.BUChecker],
    },
    {
      refetchOnMountOrArgChange: false,
    }
  );

  const { refetch: refetchGetPendingApprovers } = useGetUsersInvitationsQuery(
    {
      ...defaultParamsGetPendingUsers,
      userRole: [Roles.BUApprover],
    },
    {
      refetchOnMountOrArgChange: false,
    }
  );

  const refetchAllUsers = () => {
    if (isPSPAdminRole) {
      return Promise.all([
        refetchGetPendingUsers(),
        refetchGetPendingMakers(),
        refetchGetPendingCheckers(),
      ]);
    }

    return Promise.all([
      refetchGetPendingUsers(),
      refetchGetPendingApprovers(),
    ]);
  }


  useEffect(() => {
    return () => {
      dispatch(indetityApiAppToken.util.resetApiState());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (createUserSuccess) {
      requestProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createUserSuccess]);

  useEffect(() => {
    if (isUpdateOranisationSuccess) {
      requestChangePasswordMuation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateOranisationSuccess]);

  useEffect(() => {
    if (idGetUserSuccess && userData) {
      updateOranization({
        userId: userData.data.userId,
        orgId: userData.data.memberships[0].organisationId,
        lineOfBusiness: "Enterprise", // Fixed
        orgType: "PCL", // Fixed
        companyName: formData.bussinessUnitName,
        leiNumber: formData.bussinessUnitCode,
        orgRole: "PSP",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idGetUserSuccess, userData]);

  useEffect(() => {
    if (requestPasswordChangeSuccess) {
      toast({
        title: "Enterprise User registered successfully.",
        status: "success",
        duration: 6000,
        isClosable: true,
        position: "top-right",
      });
      setOpenModalCreate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestPasswordChangeSuccess]);

  const requestProfile = () => {
    if (createDataResonse) getUser({ userId: createDataResonse?.data?.uuid });
  };

  const requestChangePasswordMuation = async () => {
    if (createDataResonse) {
      await requestChangePasswordMutation({
        userId: createDataResonse.data.uuid,
      });
    }
  };

  const validateField = async (field: PSPField, value: string) => {
    const schemaToUse = isPaymentOpsRole ? schema : schemaWithPSPRole;
    try {
      await (schemaToUse.fields as Record<PSPField, yup.AnySchema>)[field].validate(value);
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, [field]: err.message }));
    }
  };

  const handleChange = (e: any, useTrim?: boolean) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: useTrim ? value.trim() : value,
    });

    if (name === "bussinessUnitCode") {
      validateField(name as PSPField, value);
    }
  };

  const validateForm = async () => {
    try {
      await (isPaymentOpsRole ? schema : schemaWithPSPRole).validate(formData, {
        abortEarly: false,
      });
      setErrors({});
      return true;
    } catch (err: any) {
      const validationErrors: any = {};
      err.inner.forEach((error: any) => {
        validationErrors[error.path] = error.message;
      });
      setErrors(validationErrors);
      return false;
    }
  };

  const [triggerGetUsersInvitations] = useLazyGetUsersInvitationsQuery();
  const [triggerGetUsersRole] = useGetUsersLazyQuery();

  const handleSubmit = async () => {
    const isValid = await validateForm();

    if (isValid) {
      try {
        setIsLoadingSubmit(true);
        if (isPaymentOpsRole) {
          createPSPUserMutation({
            ...formData,
          } as any);
        } else {
          const role = formData.userRole ?? "";
          const resActive = await triggerGetUsersRole({
            pageNum: DEFAULT_PAGE,
            pageSize: DEFAULT_PAGE_SIZE,
            listUserRoles: role,
            lockStatus: "false",
            orgId: organizationData?.data?.orgId ?? "",
          }).unwrap();

          const resPending = await triggerGetUsersInvitations({
            pageNumber: DEFAULT_PAGE,
            pageSize: DEFAULT_PAGE_SIZE,
            orgToken: membershipOrganisation?.token,
            userRole: [role],
          }).unwrap();

          const totalRecords =
            (resActive.paging?.totalRecords ?? 0) +
            (resPending.paging?.totalRecords ?? 0);

          const msg1 = getTotalRecordsErrorMessage(role, totalRecords);

          if (msg1) {
            dispatch(setError({ message: msg1, type: "registerUserError" }));
            history.push("/enterprise-users");
            setOpenModalCreate();
            return;
          }

          await inviteUserMutation([
            {
              emailAddress: formData.emailAddress,
              firstName: formData.firstName,
              lastName: formData.lastName,
              organisationId: organizationData?.data?.orgId,
              orgToken: membershipOrganisation?.token,
              memberships: [
                {
                  roleName: "OrganisationUser",
                  organisationId: organizationData?.data?.orgId ?? "",
                },
              ],
              roles: formData?.userRole ? [formData.userRole] : [],
            },
          ]);

          await refetchAllUsers();
        }
      } catch (error: any) {
        toast({
          title: error?.data?.message ?? "Something went wrong",
          description: "",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        setIsLoadingSubmit(false);
      }
    }
  };

  useEffect(() => {
    const validate = async () => {
      try {
        await (isPaymentOpsRole ? schema : schemaWithPSPRole).validate(
          formData,
          {
            abortEarly: false,
          }
        );
        setIsFormValid(true);
      } catch {
        setIsFormValid(false);
      }
    };

    validate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  useEffect(() => {
    if (inviteUserSuccess) {
      toast({
        title: "Enterprise User registered successfully.",
        status: "success",
        duration: 6000,
        isClosable: true,
        position: "top-right",
      });
      setOpenModalCreate();
      setIsLoadingSubmit(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteUserSuccess]);

  useEffect(() => {
    if (createUserError || inviteUserError || isUpdateOranisationError) {
      setIsLoadingSubmit(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createUserError, inviteUserError, isUpdateOranisationError]);

  return (
    <MyModal
      isOpen={isOpen}
      onClose={() => setOpenModalCreate()}
      title={`Register New ${isPaymentOpsRole ? "PSP" : "User"}`}
      submitBtnText="Register"
      cancelBtnText="Cancel"
      onSubmit={() => handleSubmit()}
      loadingSubmit={
        isLoadingSubmit
      }
      scrollBehavior="inside"
      disabledCloseOutSide
      disableSubmit={!isFormValid}
    >
      {(createUserError || inviteUserError || isUpdateOranisationError) && (
        <Alert
          data-testid="alert-error"
          status="error"
          mb="10px"
          title={
            (
              (createUserError ||
                inviteUserError ||
                updateOranisationError) as any
            )?.data?.errors
              ?.map((err: any) => {
                if (err.code === "001.S01.409.07")
                  return "Business Unit Code already exists.";

                return err.message;
              })
              ?.join(", ") ?? "Something went wrong!"
          }
        />
      )}
      <FormControl mb="12px" isInvalid={!!errors.emailAddress}>
        <FormLabel>Email Address</FormLabel>
        <Input
          value={formData.emailAddress}
          name="emailAddress"
          data-testid="email-create-enterprise"
          isRequired
          onChange={handleChange}
          disabled={isLoadingSubmit}
        />
        {errors.emailAddress && (
          <FormErrorMessage fontSize={12}>
            {errors.emailAddress}
          </FormErrorMessage>
        )}
      </FormControl>
      <FormControl mb="12px" isInvalid={!!errors.firstName}>
        <FormLabel>First Name</FormLabel>
        <Input
          value={formData.firstName}
          name="firstName"
          data-testid="firstname-create-enterprise"
          isRequired
          onChange={handleChange}
          isDisabled={isLoadingSubmit}
        />
        {errors.firstName && (
          <FormErrorMessage fontSize={12}>{errors.firstName}</FormErrorMessage>
        )}
      </FormControl>
      <FormControl mb="12px" isInvalid={!!errors.lastName}>
        <FormLabel>Last Name</FormLabel>
        <Input
          value={formData.lastName}
          name="lastName"
          data-testid="lastname-create-enterprise"
          isRequired
          onChange={handleChange}
          isDisabled={isLoadingSubmit}
        />
        {errors.lastName && (
          <FormErrorMessage fontSize={12}>{errors.lastName}</FormErrorMessage>
        )}
      </FormControl>

      {isPaymentOpsRole && (
        <>
          <FormControl mb="12px" isInvalid={!!errors.bussinessUnitName}>
            <FormLabel>Payroll Service Provider Name</FormLabel>
            <Input
              value={formData.bussinessUnitName}
              name="bussinessUnitName"
              data-testid="bussinessUnitName-create-enterprise"
              isRequired
              onChange={handleChange}
              isDisabled={isLoadingSubmit}
            />
            {errors.bussinessUnitName && (
              <FormErrorMessage fontSize={12}>
                {errors.bussinessUnitName}
              </FormErrorMessage>
            )}
          </FormControl>
          <FormControl mb="12px" isInvalid={!!errors.bussinessUnitCode}>
            <FormLabel>PSP Company Code</FormLabel>
            <Input
              value={formData.bussinessUnitCode}
              name="bussinessUnitCode"
              data-testid="bussinessUnitCode-create-enterprise"
              isRequired
              onChange={handleChange}
              isDisabled={isLoadingSubmit}
            />
            {errors.bussinessUnitCode && (
              <FormErrorMessage fontSize={12}>
                {errors.bussinessUnitCode}
              </FormErrorMessage>
            )}
          </FormControl>
        </>
      )}

      {!isPaymentOpsRole && (
        <FormControl mb="12px" isInvalid={!!errors.userRole}>
          <FormLabel>User Role</FormLabel>
          <Select
            name="userRole"
            data-testid="userRole-create-enterprise"
            isRequired
            onChange={handleChange}
            value={formData.userRole}
            isDisabled={isLoadingSubmit}
          >
            <option value="" disabled>
              --- Select a User Role ---
            </option>
            <option value={Roles.BusinessOwner}>PSP Maker</option>
            <option value={Roles.BusinessApprover}>PSP Checker</option>
          </Select>
          {errors.userRole && (
            <FormErrorMessage fontSize={12}>{errors.userRole}</FormErrorMessage>
          )}
        </FormControl>
      )}
    </MyModal>
  );
}

export default CreatePSPUser;
